import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import {
    formatSummaryTrigger,
    fullTypeTitle,
    type VirtualAppointmentSummary
} from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { VirtualAppointmentTypeTitle } from '@odo/shared-ui/clinical/virtualAppointments/VirtualAppointmentTypeTitle';
import { getTeamColor } from '@odo/shared-ui/components/teamColors';

interface VirtualAppointmentTitleProps {
    summary: VirtualAppointmentSummary;
    done: boolean;
    /** Appended to the trigger line, e.g. the done date. */
    meta?: string;
    /** Shown after the trigger line, e.g. status and team chips. */
    badges?: ReactNode;
}

// The type title (checked once done) with its trigger underneath: the header of every VA card.
function VirtualAppointmentTitleWithTrigger({
    summary,
    done,
    meta,
    badges
}: VirtualAppointmentTitleProps) {
    const subtitle = [formatSummaryTrigger(summary), meta].filter(Boolean).join(' · ');
    return (
        <span className="flex flex-col gap-0.5 min-w-0">
            <span className="flex items-center gap-1.5 min-w-0 text-[13px]">
                {done && (
                    <CheckCircleIcon
                        className="w-3.5 h-3.5 flex-shrink-0 text-green-600"
                        aria-label="Done"
                    />
                )}
                <VirtualAppointmentTypeTitle
                    summary={summary}
                    codeClassName="font-semibold text-gray-800"
                />
            </span>
            {/* Held to the text's line height; the slightly taller chips overhang into the padding. */}
            <span className="flex h-4 items-center gap-2 min-w-0">
                <span className="text-xs text-gray-500 truncate">{subtitle}</span>
                {badges}
            </span>
        </span>
    );
}

interface VirtualAppointmentAccordionItemProps {
    summary: VirtualAppointmentSummary;
    expanded: boolean;
    onToggle: () => void;
    done: boolean;
    meta?: string;
    /** Chips for the folded row; the open card shows the full controls instead. */
    foldedBadges?: ReactNode;
    /** Colours the card's left edge like the team's TeamPill; neutral without a team. */
    team?: string | null;
    children: ReactNode;
}

// The wrapper every VA card list uses: a small gap between cards, inset from the panel edges.
export const VA_CARD_LIST_CLASS = 'flex flex-col gap-1.5 p-2';

const NO_TEAM_EDGE = '#d1d5db';
// No overflow-hidden: the open card's dropdown menus must spill past its edge.
const CARD_CLASS = 'rounded-lg border border-l-[3px] bg-white transition-colors duration-200';
const EXPANDED_CARD_CLASS = 'border-gray-300 shadow-sm';
const FOLDED_CARD_CLASS = 'border-gray-200 hover:border-gray-300 hover:bg-gray-50';

// One VA as its own card in the list; the title button's overlay stretches over the header, so
// the whole header toggles.
export function VirtualAppointmentAccordionItem({
    summary,
    expanded,
    onToggle,
    done,
    meta,
    foldedBadges,
    team,
    children
}: VirtualAppointmentAccordionItemProps) {
    const bodyId = `va-body-${summary.key}`;
    return (
        <div
            className={`${CARD_CLASS} ${expanded ? EXPANDED_CARD_CLASS : FOLDED_CARD_CLASS}`}
            style={{ borderLeftColor: team ? getTeamColor(team) : NO_TEAM_EDGE }}
        >
            <div className="relative flex items-center justify-between gap-3 px-3 py-2.5">
                <button
                    type="button"
                    onClick={onToggle}
                    aria-expanded={expanded}
                    aria-controls={bodyId}
                    aria-label={fullTypeTitle(summary)}
                    className="flex flex-1 items-center gap-2 min-w-0 text-left text-[13px] focus:outline-none after:absolute after:inset-0 after:rounded-md focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-gray-400"
                >
                    <ChevronRightIcon
                        className={`w-3.5 h-3.5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
                    />
                    <VirtualAppointmentTitleWithTrigger
                        summary={summary}
                        done={done}
                        meta={meta}
                        badges={expanded ? undefined : foldedBadges}
                    />
                </button>
            </div>
            {expanded && (
                <div id={bodyId} className="flex flex-col gap-3 px-3 pb-3">
                    {children}
                </div>
            )}
        </div>
    );
}
